import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { KEYS } from 'utils/intl/constant'
import { handleFiltersChange, translate } from 'utils/intl/helper'
import { decompress } from 'object-compressor'
import { addSelected, removeSelected } from '../../../store/reducer/candidate'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import constant from '../../utils/constant'
import { selectBrandTheme } from '../../../store/reducer/ui'
import Filter from '../../page/left/Filter'
import Content from '../../component/template/Content'
import Aside from '../../component/template/Aside'
import DetailsHeader from './components/DetailsHeader'
import CandidatesTable from './components/CandidatesTable'

const StyledLoading = styled.div`
    width: 100%;
    height: 30%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 50px 0;
`

const StyledBox = styled.div`
    .MuiTableCell-root {
        color: #757575;
    }
`

const StyledGrid = styled(Grid)`
    min-height: calc(100vh - 190px);
    @media (max-width: 1200px) {
        min-height: calc(100vh - 280px);
    }
`

export default function Details() {
    const dispatch = useAppDispatch()
    const intl = useAppSelector((state) => state.ui.intl)
    const company = useAppSelector((state) => selectBrandTheme(state.ui))
    const { selectedModel } = useParams()

    const [listFiltered, setListFiltered] = useState<any[] | null>(null)
    const [totalRows, setTotalRows] = useState(0)
    const [page, setPage] = useState(1)
    const [rowsPerPage] = useState(10)
    const [isLoadingModel, setIsLoadingModel] = useState(true)

    const [checkedOptions, setCheckedOptions] = useState<any[] | undefined>(undefined)
    const [checkedSizes, setCheckedSizes] = useState<any[] | undefined>(undefined)
    const [checkedCalcTypes, setCheckedCalcTypes] = useState<any[] | undefined>(undefined)
    const [rangeIndices, setRangeIndices] = useState<string[]>([])
    const [currentRangeIndex, setCurrentRangeIndex] = useState<string | undefined>(undefined)
    const [expanded, setExpanded] = useState('option')

    const [selectedId, setSelectedId] = useState<string>('')

    const findSelected = (id: string) => listFiltered?.find((row) => row.id === id)

    const handleRowSelect = (id: string) => {
        const newSelectedId = selectedId !== id ? id : ''
        setSelectedId(newSelectedId)
        const selectedRow = newSelectedId ? findSelected(newSelectedId) : null
        if (selectedRow) {
            dispatch(addSelected(selectedRow))
        } else {
            dispatch(removeSelected())
        }
    }

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage)
    }

    const fetchRangeIndex = useCallback(async () => {
        try {
            const { data } = await axios.get(constant.backend.SEARCH_MODEL + selectedModel, {
                params: {
                    company,
                },
            })
            setRangeIndices(data.uniqueRangeIndices)
            setCurrentRangeIndex(data.uniqueRangeIndices[data.uniqueRangeIndices?.length - 1] || '')
        } catch (error) {
            console.error(error)
        }
    }, [selectedModel, company])

    const fetchOptions = useCallback(async () => {
        try {
            const { data } = await axios.get(constant.backend.OPTIONS_BY_MODEL + selectedModel, {
                params: {
                    company,
                },
            })
            const uniqueOptions = [...new Set(data.map(JSON.stringify))].map(JSON.parse)

            setCheckedOptions((prev) =>
                uniqueOptions.map((option: any) => ({
                    ...option,
                    checked: prev?.find((opt) => opt.key === option.key)?.checked ?? false,
                }))
            )
        } catch (error) {
            console.error(error)
        }
    }, [selectedModel, company])

    const fetchSizes = useCallback(async () => {
        try {
            const { data } = await axios.get(constant.backend.SIZES_BY_MODEL + selectedModel, {
                params: {
                    company,
                    rangeIndex: currentRangeIndex,
                },
            })
            const uniqueSizes = [...new Set(data.map(JSON.stringify))].map(JSON.parse)

            setCheckedSizes((prev) =>
                uniqueSizes.map((size: string) => ({
                    label: size,
                    checked: prev?.find((s) => s.label === size)?.checked ?? false,
                }))
            )
        } catch (error) {
            console.error(error)
        }
    }, [selectedModel, company, currentRangeIndex])

    const fetchCalcTypes = useCallback(async () => {
        try {
            const { data } = await axios.get(constant.backend.CALC_TYPES_BY_MODEL + selectedModel, {
                params: {
                    company,
                },
            })
            const uniqueCalcTypes = [...new Set(data.map(JSON.stringify))].map(JSON.parse)

            setCheckedCalcTypes((prev) =>
                uniqueCalcTypes.map((type: string) => ({
                    label: type,
                    checked: prev?.find((c) => c.label === type)?.checked ?? false,
                }))
            )
        } catch (error) {
            console.error(error)
        }
    }, [selectedModel, company])

    const fetchData = useCallback(
        async (checkedOptionsParam, checkedSizesParam, checkedCalcTypesParam) => {
            setIsLoadingModel(true)

            let params: any = {
                company,
                rangeIndex: currentRangeIndex,
            }

            if (checkedOptionsParam.filter((o) => o.checked).length > 0) {
                params = {
                    ...params,
                    options: checkedOptionsParam
                        .filter((option: any) => option.checked)
                        .map((option: any) => option.key)
                        .join(','),
                }
            }

            if (checkedSizesParam.filter((s) => s.checked).length > 0) {
                params = {
                    ...params,
                    sizes: checkedSizesParam
                        .filter((size: any) => size.checked)
                        .map((size: any) => size.label)
                        .join(','),
                }
            }

            if (checkedCalcTypesParam.filter((c) => c.checked).length > 0) {
                params = {
                    ...params,
                    types: checkedCalcTypesParam
                        .filter((type: any) => type.checked)
                        .map((type: any) => type.label)
                        .join(','),
                }
            }

            try {
                const { data } = await axios.get(
                    constant.backend.SEARCH_MODEL + selectedModel + `/${rowsPerPage}/${page}`,
                    { params }
                )
                setTotalRows(data.totalResult)
                const resultData = data?.result?.map((obj: any) => decompress(obj))
                setListFiltered(resultData ?? [])
                setSelectedId('')
            } catch (error) {
                console.error(error)
                setListFiltered([])
            } finally {
                setIsLoadingModel(false)
            }
        },
        [page, currentRangeIndex, rowsPerPage, company, selectedModel]
    )

    useEffect(() => {
        fetchRangeIndex()
    }, [fetchRangeIndex])

    useEffect(() => {
        if (currentRangeIndex) {
            fetchCalcTypes()
            fetchOptions()
            fetchSizes()
        }
    }, [currentRangeIndex, fetchCalcTypes, fetchOptions, fetchSizes])

    const prevFiltersRef = React.useRef({
        checkedCalcTypes,
        checkedOptions,
        checkedSizes,
    })

    useEffect(() => {
        prevFiltersRef.current = handleFiltersChange(
            prevFiltersRef.current,
            { checkedSizes, checkedOptions, checkedCalcTypes },
            () => setPage(1)
        )
    }, [checkedSizes, checkedOptions, checkedCalcTypes])

    useEffect(() => {
        if (checkedSizes && checkedOptions && checkedCalcTypes) {
            fetchData(checkedOptions, checkedSizes, checkedCalcTypes)
        }
    }, [checkedSizes, checkedOptions, checkedCalcTypes, page, fetchData])

    return (
        <StyledGrid container>
            <Aside>
                <Filter
                    checkedOptions={checkedOptions}
                    setCheckedOptions={setCheckedOptions}
                    checkedSizes={checkedSizes}
                    setCheckedSizes={setCheckedSizes}
                    checkedCalcTypes={checkedCalcTypes}
                    setCheckedCalcTypes={setCheckedCalcTypes}
                    expanded={expanded}
                    setExpanded={setExpanded}
                />
            </Aside>
            <Content>
                {isLoadingModel || listFiltered === null ? (
                    <StyledLoading>
                        <CircularProgress disableShrink />
                    </StyledLoading>
                ) : listFiltered.length > 0 ? (
                    <StyledBox>
                        <DetailsHeader
                            defaultData={listFiltered[0]}
                            currentRangeIndex={currentRangeIndex}
                            onRangeIndexChange={setCurrentRangeIndex}
                            rangeIndices={rangeIndices}
                        />

                        <CandidatesTable
                            rows={listFiltered}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            totalRows={totalRows}
                            selectedId={selectedId}
                            onRowSelect={handleRowSelect}
                            onChangePage={handleChangePage}
                        />
                    </StyledBox>
                ) : (
                    <StyledBox>
                        <Paper elevation={0}>
                            <Typography variant='body2' color='textSecondary'>
                                {translate(intl, KEYS.UI_NO_RESULTS_FOUND)}
                            </Typography>
                        </Paper>
                    </StyledBox>
                )}
            </Content>
        </StyledGrid>
    )
}
